// frontend/src/components/ClientCard.jsx
// Displays a single client row on the dashboard with their visit progress.

import { useState } from 'react';
import { api } from '../utils/api';
import { useLanguage } from '../utils/i18n';
import { useNotification } from '../utils/NotificationContext';
import { useAuth } from '../utils/AuthContext';
import ConfirmModal from './ConfirmModal';
const BACKEND_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

function formatTimeAgo(dateStr, t) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return t('time_just_now');
  if (mins < 60) return `${mins}m ${t('time_ago')}`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ${t('time_ago')}`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ${t('time_ago')}`;
  const months = Math.floor(days / 30);
  return `${months}mo ${t('time_ago')}`;
}

export default function ClientCard({ client, onRefresh }) {
  const { t } = useLanguage();
  const { showNotification } = useNotification();
  const { isDemo } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const pct = Math.min(100, (client.currentCount / client.threshold) * 100);
  const isEligible = client.currentCount >= client.threshold;

  async function handleRecordVisit() {
    setLoading(true);
    try {
      await api.recordVisit(client.id);
      showNotification(`${t('notify_visit_recorded')} - ${client.name}`, 'success');
      onRefresh();
    } catch (err) {
      showNotification(err.message, 'error');
    } finally {
      setLoading(false);
    }
  }

  async function handleRedeemReward() {
    setLoading(true);
    try {
      await api.redeemReward(client.id);
      showNotification(`${t('notify_reward_redeemed')} - ${client.name}`, 'success');
      onRefresh();
    } catch (err) {
      showNotification(err.message, 'error');
    } finally {
      setLoading(false);
    }
  }

  async function handleRemoveVisit() {
    if (client.currentCount <= 0) return;
    setIsConfirmOpen(true);
  }

  async function confirmRemoveVisit() {
    setIsConfirmOpen(false);
    setLoading(true);
    try {
      await api.removeVisit(client.id);
      showNotification(`${t('notify_visit_removed')} - ${client.name}`, 'success');
      onRefresh();
    } catch (err) {
      showNotification(err.message, 'error');
    } finally {
      setLoading(false);
    }
  }

  const onDemoClick = (e) => {
    if (isDemo) {
      e.preventDefault();
      showNotification(t('demo_feature_restricted') || 'Wallet integration is disabled in demo mode.', 'info');
    }
  };

  return (
    <div className="glass-card hover-lift" style={{ position: 'relative' }}>
      <ConfirmModal
        isOpen={isConfirmOpen}
        title={t('confirm_remove_title')}
        message={`${t('remove_visit_confirm')} ${client.name}?`}
        onConfirm={confirmRemoveVisit}
        onCancel={() => setIsConfirmOpen(false)}
      />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
        <div>
          <h3 style={{ fontSize: 18, fontWeight: 700, color: 'var(--text)' }}>{client.name}</h3>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>
            {client.email || client.phone || t('no_contact_info')}
          </p>
          <p style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 2 }}>
            {client.lastVisit
              ? `${t('last_visit')}: ${formatTimeAgo(client.lastVisit, t)}`
              : t('no_visits_yet')}
          </p>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 24, fontWeight: 800, color: isEligible ? 'var(--success)' : 'var(--text)' }}>
            {client.currentCount} <span style={{ fontSize: 14, color: 'var(--text-muted)', fontWeight: 500 }}>/ {client.threshold}</span>
          </div>
        </div>
      </div>

      <div style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 8, color: 'var(--text-muted)', fontWeight: 500 }}>
          <span>{t('progress_toward_reward')}</span>
          <span>{client.threshold - client.currentCount > 0 ? `${client.threshold - client.currentCount} ${t('visits')}` : t('reward_due')}</span>
        </div>
        <div className="progress-bar">
          <div className="progress-fill" style={{ width: `${pct}%`, background: isEligible ? 'var(--success)' : 'var(--primary)' }} />
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button
          className={isEligible ? "btn btn-success btn-sm btn-glow" : "btn btn-primary btn-sm"}
          onClick={isEligible ? handleRedeemReward : handleRecordVisit}
          disabled={loading}
          style={{ flex: 2, justifyContent: 'center' }}
        >
          {isEligible ? t('gift') : t('record_visit')}
        </button>
        <button
          className="btn btn-outline btn-sm glass-btn"
          onClick={handleRemoveVisit}
          disabled={loading || client.currentCount <= 0}
          style={{ flex: 1, justifyContent: 'center' }}
        >
          {t('remove_visit')}
        </button>
      </div>

      {/* Wallet Buttons */}
      <div style={{ display: 'flex', gap: 8, marginTop: 12, width: '100%' }}>
        {client.walletLink ? (
          <a
            href={isDemo ? '#' : client.walletLink}
            onClick={onDemoClick}
            target="_blank"
            rel="noreferrer"
            className="btn btn-outline btn-sm glass-btn"
            style={{
              flex: 1,
              justifyContent: 'center',
              background: '#fff',
              color: '#000',
              border: 'none'
            }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16" style={{ marginRight: 8 }}><path fill="#4285F4" d="M21.2 8.9l-1.7-3a.5.5 0 0 0-.7-.2l-1.7 1a.5.5 0 0 1-.7-.2L14.7 3.5a.5.5 0 0 0-.7-.2L12.3 4.3a.5.5 0 0 1-.7-.2L10.9 2.4a.5.5 0 0 0-.4-.3.5.5 0 0 0-.4.1L2.8 8.3a.5.5 0 0 0-.1.7l1.7 3a.5.5 0 0 0 .7.2L12 8l4 6.9" /><path fill="#EA4335" d="M16 14.9l-4-6.9-6.9 4a.5.5 0 0 1-.7-.2L2.8 9a.5.5 0 0 0-.1.7l5.1 8.8a.5.5 0 0 0 .7.2l1.7-1a.5.5 0 0 1 .7.2l1.7 3a.5.5 0 0 0 .7.2l1.7-1a.5.5 0 0 1 .7.2l.7 1.2a.5.5 0 0 0 .7.2l1.7-1a.5.5 0 0 0 .2-.7L16 14.9z" /></svg>
            {t('add_to_wallet')}
          </a>
        ) : (
          <button
            disabled
            className="btn btn-outline btn-sm glass-btn"
            style={{
              flex: 1,
              justifyContent: 'center',
              background: '#e5e7eb',
              color: '#9ca3af',
              border: 'none',
              cursor: 'not-allowed'
            }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16" style={{ marginRight: 8, filter: 'grayscale(100%)', opacity: 0.5 }}><path fill="#4285F4" d="M21.2 8.9l-1.7-3a.5.5 0 0 0-.7-.2l-1.7 1a.5.5 0 0 1-.7-.2L14.7 3.5a.5.5 0 0 0-.7-.2L12.3 4.3a.5.5 0 0 1-.7-.2L10.9 2.4a.5.5 0 0 0-.4-.3.5.5 0 0 0-.4.1L2.8 8.3a.5.5 0 0 0-.1.7l1.7 3a.5.5 0 0 0 .7.2L12 8l4 6.9" /><path fill="#EA4335" d="M16 14.9l-4-6.9-6.9 4a.5.5 0 0 1-.7-.2L2.8 9a.5.5 0 0 0-.1.7l5.1 8.8a.5.5 0 0 0 .7.2l1.7-1a.5.5 0 0 1 .7.2l1.7 3a.5.5 0 0 0 .7.2l1.7-1a.5.5 0 0 1 .7.2l.7 1.2a.5.5 0 0 0 .7.2l1.7-1a.5.5 0 0 0 .2-.7L16 14.9z" /></svg>
            {t('add_to_wallet')}
          </button>
        )}

        <a
          href={isDemo ? '#' : `${BACKEND_URL}/apple-pass/${client.walletObjectId}`}
          onClick={onDemoClick}
          target="_blank"
          rel="noreferrer"
          className="btn btn-outline btn-sm glass-btn"
          style={{
            flex: 1,
            justifyContent: 'center',
            background: '#000',
            color: '#fff',
            border: 'none',
            textDecoration: 'none',
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <svg viewBox="0 0 384 512" width="14" height="14" fill="currentColor" style={{ marginRight: 8 }}>
            <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
          </svg>
          {t('add_to_apple_wallet')}
        </a>
      </div>
    </div>
  );
}
